import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { Content } from 'src/app/models/content';
import { ContentContainer } from 'src/app/models/container';
import { ContainerService } from 'src/app/services/container/container.service';
import { ContentService } from 'src/app/services/content/content.service';

export type WhatsNewItem =
  | { kind: 'content'; data: Content; show: boolean }
  | { kind: 'container'; data: ContentContainer; show: boolean };

@Injectable({
  providedIn: 'root',
})
export class WhatsNewService {
  whatsNewSubject: BehaviorSubject<WhatsNewItem[]> = new BehaviorSubject<WhatsNewItem[]>([]);

  constructor(
    private contentService: ContentService,
    private containerService: ContainerService
  ) {}

  getWhatsNew(): Observable<WhatsNewItem[]> {
    return this.whatsNewSubject.asObservable();
  }

  getWhatsNewItems(): WhatsNewItem[] {
    return this.whatsNewSubject.getValue();
  }

  setWhatsNewItems(items: WhatsNewItem[]) {
    this.whatsNewSubject.next(items);
  }

  /** Reload What's New from API and push to subject so home page updates immediately. */
  async refreshWhatsNew(): Promise<void> {
    try {
      const [contentRes, containersRes] = await Promise.all([
        firstValueFrom(this.contentService.getNewestWithWhatsNew()),
        firstValueFrom(this.containerService.getContainersWhatsNew()),
      ]);
      const now = new Date().getTime();
      const isWhatsNewEndPast = (endDateTime: string | null | undefined) =>
        !!endDateTime && new Date(endDateTime).getTime() < now;

      const contentList: Content[] = (contentRes || []).filter(
        (c: Content) =>
          c.status === 'PUBLISHED' &&
          c.type !== 'EVENT' &&
          c.type !== 'BANNER' &&
          !!c.showWhatsNew &&
          !isWhatsNewEndPast(c.whatsNewEndDateTime)
      );

      const containerList: ContentContainer[] = (containersRes || []).filter(
        (cont: ContentContainer) => !isWhatsNewEndPast(cont.whatsNewEndDateTime)
      );

      const contentItems: WhatsNewItem[] = contentList.map((c) => ({
        kind: 'content' as const,
        data: c,
        show: false,
      }));
      const containerItems: WhatsNewItem[] = containerList.map((cont) => ({
        kind: 'container' as const,
        data: cont,
        show: false,
      }));
      const items = [...contentItems, ...containerItems];
      this.sortItemsByName(items);
      this.whatsNewSubject.next(items);
    } catch (err) {
      console.error('WhatsNewService.refreshWhatsNew', err);
    }
  }

  private sortItemsByName(items: WhatsNewItem[]) {
    const toName = (item: WhatsNewItem) => {
      const name = item.data.whatsNewHeading ?? item.data.name ?? '';
      return String(name).toLowerCase();
    };
    items.sort((a, b) => toName(a).localeCompare(toName(b)));
  }
}
